import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    appName: 'DowntimeIQ',
    dataRetentionDays: 90,
    autoBackup: true,
    sessionTimeout: 30,
    theme: 'light',
    language: 'en'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    try {
      const stored = localStorage.getItem('systemSettings');
      if (stored) {
        setSettings(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = () => {
    try {
      localStorage.setItem('systemSettings', JSON.stringify(settings));
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">System Settings</h2>
        <p className="text-sm text-muted-foreground">Configure application preferences and behavior</p>
      </div>
      <div className="space-y-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">AI Integration</h3>
              <p className="text-xs text-muted-foreground mt-1">AI features are powered by a secure server-side integration</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-success/10 rounded-full">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-xs font-medium text-success">Secured</span>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-success/5 rounded-lg border border-success/20">
            <Icon name="Lock" size={18} className="text-success mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">API keys are stored server-side only</p>
              <p className="text-xs text-muted-foreground mt-1">
                All AI requests are proxied through a secure backend. Your OpenAI API key is never exposed to the browser or stored in localStorage.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="Database" size={20} className="text-accent" />
            </div>
            <h3 className="font-semibold text-foreground">Data Management</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Data Retention (Days)
              </label>
              <Input
                type="number"
                value={settings?.dataRetentionDays}
                onChange={(e) => handleChange('dataRetentionDays', parseInt(e?.target?.value))}
                placeholder="90"
                min="1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Automatically delete data older than specified days
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <p className="font-medium text-sm text-foreground">Auto Backup</p>
                <p className="text-xs text-muted-foreground">Automatically backup data daily</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.autoBackup}
                  onChange={(e) => handleChange('autoBackup', e?.target?.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-success" />
            </div>
            <h3 className="font-semibold text-foreground">Security Settings</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Session Timeout (Minutes)
              </label>
              <Input
                type="number"
                value={settings?.sessionTimeout}
                onChange={(e) => handleChange('sessionTimeout', parseInt(e?.target?.value))}
                placeholder="30"
                min="5"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Automatically log out after period of inactivity
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={loadSettings}>
            Reset
          </Button>
          <Button variant="default" iconName="Save" onClick={saveSettings}>
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;